/**
 * The manufacturer's binding of the shared {@link TierVocabularySection}:
 * `movement_manufacturers/{id}.tierVocabulary`, plus ✨ Suggest tier names.
 *
 * Generic grades only — the manufacturer's own ladder (Sellita: Standard →
 * Elaboré → Premium → Chronometer). Brand-specific tiers live on the calibre
 * (James, 2026-10-29); validateTierVocabulary refuses a brand-scoped entry.
 */

import { useMemo } from 'react';
import type { TierVocabularyEntry } from '@watchkeeper/shared-types';
import type { MovementManufacturer } from '../../types';
import { TierVocabularySection, type TierVocabularyOwner } from '../TierVocabulary/TierVocabularySection';
import {
  mutateManufacturerTierVocabulary,
  suggestManufacturerTierVocabularyWithAI,
} from '../../services/firebase';

interface Props {
  manufacturer: MovementManufacturer;
  /** Lets the host refresh its copy after a write, as the other sections do. */
  onManufacturerChange?: (next: MovementManufacturer) => void;
}

const NONE: TierVocabularyEntry[] = [];

export function ManufacturerTierNamesSection({ manufacturer, onManufacturerChange }: Props) {
  const name = manufacturer.displayName || manufacturer.name || manufacturer.id;
  const owner: TierVocabularyOwner = useMemo(
    () => ({
      kind: 'manufacturer',
      id: manufacturer.id,
      name,
      entries: manufacturer.tierVocabulary ?? NONE,
      mutate: async (change) => {
        const next = await mutateManufacturerTierVocabulary(manufacturer.id, change);
        onManufacturerChange?.({ ...manufacturer, tierVocabulary: next });
        return next;
      },
      suggest: (hint) =>
        suggestManufacturerTierVocabularyWithAI({
          manufacturerId: manufacturer.id,
          ...(hint ? { additionalContext: hint } : {}),
        }),
    }),
    [manufacturer, name, onManufacturerChange],
  );

  return (
    <TierVocabularySection
      owner={owner}
      intro={
        <>
          The manufacturer&rsquo;s official grade names and their other spellings, base → premium. Calibre
          families add to these for their own calibres. Generic grades only: a brand&rsquo;s own modification is
          a brand-specific tier on that calibre, not a name here.
        </>
      }
    />
  );
}
